import { Controller } from "@hotwired/stimulus"

const SDK_SRC = "https://sdk.mercadopago.com/js/v2"

export default class extends Controller {
  static targets = ["token", "brand", "number", "name", "expiry", "cvv", "cpf", "nationality"]
  static values = { publicKey: String }

  async pay(event) {
    if (this.tokenTarget.value) return
    if (this.submitting) {
      event.preventDefault()
      return
    }

    this.submitting = true
    event.preventDefault()
    this.disableSubmit()
    if (!this.publicKeyValue) return this.element.submit()

    try {
      await this.ensureSdk()
      const mp = new window.MercadoPago(this.publicKeyValue, { locale: "pt-BR" })
      const number = this.digits(this.numberTarget.value)
      const expiry = this.parseExpiry(this.expiryTarget.value)
      const cpf = this.payerIsForeign() ? "" : this.digits(this.hasCpfTarget ? this.cpfTarget.value : "")

      if (number.length < 13 || !expiry || this.cvvTarget.value.length < 3) {
        throw new Error("Confira os dados do cartão.")
      }

      const methods = await mp.getPaymentMethods({ bin: number.slice(0, 6) })
      const brand = methods?.results?.[0]?.id
      if (!brand) throw new Error("Bandeira do cartão não identificada.")

      const cardToken = await mp.createCardToken({
        cardNumber: number,
        cardholderName: this.nameTarget.value.trim(),
        cardExpirationMonth: expiry.month,
        cardExpirationYear: expiry.year,
        securityCode: this.cvvTarget.value.trim(),
        identificationType: cpf.length === 11 ? "CPF" : undefined,
        identificationNumber: cpf.length === 11 ? cpf : undefined
      })

      const token = cardToken?.id || cardToken?.token
      if (!token) throw new Error("Não foi possível tokenizar o cartão.")

      this.tokenTarget.value = token
      this.brandTarget.value = brand
      this.numberTarget.value = ""
      this.cvvTarget.value = ""
      this.element.requestSubmit()
    } catch (error) {
      this.submitting = false
      this.enableSubmit()
      window.alert(error?.message || "Não foi possível processar o cartão. Tente novamente.")
    }
  }

  disableSubmit() {
    this.disableSubmitController?.disable()
  }

  enableSubmit() {
    this.disableSubmitController?.enable()
  }

  get disableSubmitController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "disable-submit")
  }

  payerIsForeign() {
    if (!this.hasNationalityTarget) return false

    const checked = this.nationalityTargets.find((input) => input.checked)
    return checked?.value === "foreign"
  }

  parseExpiry(value) {
    const digits = this.digits(value)
    if (digits.length < 4) return null

    const month = digits.slice(0, 2)
    let year = digits.slice(2)
    if (year.length === 2) year = `20${year}`
    return { month, year }
  }

  digits(value) {
    return value.toString().replace(/\D/g, "")
  }

  ensureSdk() {
    if (window.MercadoPago) return Promise.resolve()
    if (this.sdkPromise) return this.sdkPromise

    this.sdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = SDK_SRC
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => reject(new Error("Não foi possível carregar o Mercado Pago."))
      document.head.appendChild(script)
    })

    return this.sdkPromise
  }
}
